import React from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import Card from '../../../components/ui/Card';


export default function PeopleTab({ lang, year, petrobrasData, refDate, t }) {
  const src = petrobrasData;
  const fallbackNotice = null;

  const pcdRows       = src['empregado_PCD'] || [];
  const posGeneroRows = src['empregado_posicao_declaracao_genero'] || [];
  const posRacaRows   = src['empregado_posicao_declaracao_raca'] || [];
  const posFaixaRows  = src['empregado_posicao_faixa_etaria'] || [];
  const posLocalRows  = src['empregado_posicao_local'] || [];
  const locGeneroRows = src['empregado_local_declaracao_genero'] || [];

  const GENDER_COLORS = { Feminino: '#ec4899', Masculino: '#3b82f6', 'Não Binário': '#8b5cf6', Outros: '#6b7280', 'Sem Resp.': '#d1d5db' };
  const RACE_COLORS   = { Branco: '#6b7280', Pardo: '#d97706', Preto: '#374151', Amarelo: '#fbbf24', Indígena: '#10b981', Outros: '#6b7280', 'Sem Resp.': '#d1d5db' };
  const AGE_COLORS    = { '≤30': '#10b981', '30–50': '#3b82f6', '>50': '#f59e0b' };
  const REGION_COLORS = ['#10b981','#3b82f6','#f59e0b','#ec4899','#8b5cf6','#06b6d4'];

  const totalGenero = { F: 0, M: 0, NB: 0 };
  posGeneroRows.forEach(r => {
    totalGenero.F  += r.Quantidade_Feminino || 0;
    totalGenero.M  += r.Quantidade_Masculino || 0;
    totalGenero.NB += (r.Quantidade_Nao_Binario || 0) + (r.Quantidade_Outros || 0);
  });
  const totalGeneroAll = totalGenero.F + totalGenero.M + totalGenero.NB;

  const totalRaca = { Branco: 0, Pardo: 0, Preto: 0, Amarelo: 0, 'Indígena': 0 };
  posRacaRows.forEach(r => {
    totalRaca.Branco    += r.Quantidade_Branco || 0;
    totalRaca.Pardo     += r.Quantidade_Pardo || 0;
    totalRaca.Preto     += r.Quantidade_Preto || 0;
    totalRaca.Amarelo   += r.Quantidade_Amarelo || 0;
    totalRaca['Indígena'] += r.Quantidade_Indigena || 0;
  });

  const totalFaixa = { '≤30': 0, '30–50': 0, '>50': 0 };
  posFaixaRows.forEach(r => {
    totalFaixa['≤30']   += r.Quantidade_Ate30Anos || 0;
    totalFaixa['30–50'] += r.Quantidade_30a50Anos || 0;
    totalFaixa['>50']   += r.Quantidade_Acima50Anos || 0;
  });
  const totalFaixaAll = totalFaixa['≤30'] + totalFaixa['30–50'] + totalFaixa['>50'];

  const pcdTotal      = pcdRows.reduce((s, r) => s + (r.Quantidade_PCD || 0), 0);
  const nonPcdTotal   = pcdRows.reduce((s, r) => s + (r.Quantidade_Nao_PCD || 0), 0);
  const totalHeadcount = pcdTotal + nonPcdTotal;

  const genderPieData = [
    { name: lang === 'pt' ? 'Feminino' : 'Female', value: totalGenero.F },
    { name: lang === 'pt' ? 'Masculino' : 'Male', value: totalGenero.M },
    { name: lang === 'pt' ? 'Não Binário/Outros' : 'Non-Binary/Other', value: totalGenero.NB },
  ].filter(d => d.value > 0);

  const racaPieData = Object.entries(totalRaca).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);

  const agePieData = Object.entries(totalFaixa).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);

  const generoBarData = posGeneroRows.map(r => ({
    posicao: r.Posicao,
    [lang === 'pt' ? 'Feminino' : 'Female']: r.Quantidade_Feminino || 0,
    [lang === 'pt' ? 'Masculino' : 'Male']: r.Quantidade_Masculino || 0,
    [lang === 'pt' ? 'Não Binário' : 'Non-Binary']: (r.Quantidade_Nao_Binario || 0) + (r.Quantidade_Outros || 0),
  }));

  const racaBarData = posRacaRows.map(r => ({
    posicao: r.Posicao,
    Branco: r.Quantidade_Branco || 0,
    Pardo: r.Quantidade_Pardo || 0,
    Preto: r.Quantidade_Preto || 0,
    Amarelo: r.Quantidade_Amarelo || 0,
    'Indígena': r.Quantidade_Indigena || 0,
  }));

  const faixaBarData = posFaixaRows.map(r => ({
    posicao: r.Posicao,
    '≤30': r.Quantidade_Ate30Anos || 0,
    '30–50': r.Quantidade_30a50Anos || 0,
    '>50': r.Quantidade_Acima50Anos || 0,
  }));

  const regionalBarData = posLocalRows.map(r => ({
    posicao: r.Posicao,
    Norte: r.Quantidade_Norte || 0,
    Nordeste: r.Quantidade_Nordeste || 0,
    'Centro-Oeste': r.Quantidade_Centro_Oeste || 0,
    Sudeste: r.Quantidade_Sudeste || 0,
    Sul: r.Quantidade_Sul || 0,
    Exterior: r.Quantidade_Exterior || 0,
  }));

  const SimplePieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-[var(--card)] border border-[var(--border)] p-2.5 rounded-xl shadow-lg text-xs space-y-0.5 z-[100]">
        <p className="font-bold text-[var(--text)]">{d.name}</p>
        <p className="text-emerald-400 font-semibold">{Number(d.value).toLocaleString('pt-BR')}</p>
      </div>
    );
  };

  const MiniDonut = ({ data, colors, total }) => (
    <div className="relative h-[140px]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Tooltip content={<SimplePieTooltip />} wrapperStyle={{ zIndex: 1000 }} />
          <Pie data={data} cx="50%" cy="50%" innerRadius={38} outerRadius={56} paddingAngle={2} dataKey="value">
            {data.map((_, i) => <Cell key={i} fill={Object.values(colors)[i % Object.values(colors).length]} />)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-[9px] text-[var(--muted)] font-semibold uppercase">Total</span>
        <span className="text-xs font-black text-[var(--text)]">{Number(total).toLocaleString('pt-BR')}</span>
      </div>
    </div>
  );

  const PeopleLegend = ({ data, colors }) => (
    <div className="space-y-1 mt-2">
      {data.map((d, i) => {
        const total = data.reduce((s, x) => s + x.value, 0);
        const pct = total > 0 ? (d.value / total * 100).toFixed(1) : '0.0';
        const color = Object.values(colors)[i % Object.values(colors).length];
        return (
          <div key={i} className="flex items-center justify-between text-[10px]">
            <div className="flex items-center gap-1.5 truncate">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
              <span className="text-[var(--text)] truncate">{d.name}</span>
            </div>
            <div className="flex gap-2 flex-shrink-0 ml-2">
              <span className="text-[var(--muted)] font-mono">{Number(d.value).toLocaleString('pt-BR')}</span>
              <span className="text-yellow-500 font-bold w-[38px] text-right">{pct}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );

  if (posGeneroRows.length === 0 && posRacaRows.length === 0 && posFaixaRows.length === 0) {
    return (
      <div className="p-8 text-center text-[var(--muted)]">
        {lang === 'pt' ? 'Dados de Pessoas não disponíveis.' : 'People data not available.'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {fallbackNotice}

      {/* Headcount KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: lang === 'pt' ? 'Total Empregados' : 'Total Employees', value: Number(totalHeadcount).toLocaleString('pt-BR'), color: 'text-emerald-500' },
          { label: lang === 'pt' ? 'Pessoas com Deficiência' : 'Persons with Disability', value: Number(pcdTotal).toLocaleString('pt-BR'), color: 'text-blue-400' },
          { label: lang === 'pt' ? '% PCD' : 'PCD %', value: totalHeadcount > 0 ? `${(pcdTotal/totalHeadcount*100).toFixed(2)}%` : '-', color: 'text-yellow-500' },
          { label: lang === 'pt' ? 'Mulheres (total)' : 'Women (total)', value: totalGeneroAll > 0 ? `${(totalGenero.F/totalGeneroAll*100).toFixed(1)}%` : '-', color: 'text-pink-400' },
        ].map((kpi, i) => (
          <div key={i} className="bg-[var(--card)] border border-[var(--border)] rounded-2xl p-4 space-y-1">
            <span className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider block">{kpi.label}</span>
            <span className={`text-xl font-black block ${kpi.color}`}>{kpi.value}</span>
          </div>
        ))}
      </div>

      {/* Gender / Race / Age donuts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        <Card title={lang === 'pt' ? 'Gênero' : 'Gender'} refDate={refDate}>
          <MiniDonut data={genderPieData} colors={GENDER_COLORS} total={totalGeneroAll} />
          <PeopleLegend data={genderPieData} colors={GENDER_COLORS} />
          <div className="mt-4 pt-3 border-t border-[var(--border)]">
            <p className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider mb-2">{lang === 'pt' ? 'Por Posição' : 'By Position'}</p>
            <div className="h-28">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={generoBarData} layout="vertical" margin={{ top: 0, right: 8, left: 50, bottom: 0 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="posicao" tick={{ fill: 'var(--muted)', fontSize: 9 }} width={50} tickLine={false} axisLine={false} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '10px' }} />
                  <Bar dataKey={lang === 'pt' ? 'Feminino' : 'Female'} stackId="a" fill="#ec4899" />
                  <Bar dataKey={lang === 'pt' ? 'Masculino' : 'Male'} stackId="a" fill="#3b82f6" />
                  <Bar dataKey={lang === 'pt' ? 'Não Binário' : 'Non-Binary'} stackId="a" fill="#8b5cf6" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>

        <Card title={lang === 'pt' ? 'Raça / Cor' : 'Race / Ethnicity'} refDate={refDate}>
          <MiniDonut data={racaPieData} colors={RACE_COLORS} total={Object.values(totalRaca).reduce((a,b)=>a+b,0)} />
          <PeopleLegend data={racaPieData} colors={RACE_COLORS} />
          <div className="mt-4 pt-3 border-t border-[var(--border)]">
            <p className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider mb-2">{lang === 'pt' ? 'Por Posição' : 'By Position'}</p>
            <div className="h-28">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={racaBarData} layout="vertical" margin={{ top: 0, right: 8, left: 50, bottom: 0 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="posicao" tick={{ fill: 'var(--muted)', fontSize: 9 }} width={50} tickLine={false} axisLine={false} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '10px' }} />
                  <Bar dataKey="Branco" stackId="a" fill="#6b7280" />
                  <Bar dataKey="Pardo" stackId="a" fill="#d97706" />
                  <Bar dataKey="Preto" stackId="a" fill="#374151" />
                  <Bar dataKey="Amarelo" stackId="a" fill="#fbbf24" />
                  <Bar dataKey="Indígena" stackId="a" fill="#10b981" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>

        <Card title={lang === 'pt' ? 'Faixa Etária' : 'Age Range'} refDate={refDate}>
          <MiniDonut data={agePieData} colors={AGE_COLORS} total={totalFaixaAll} />
          <PeopleLegend data={agePieData} colors={AGE_COLORS} />
          <div className="mt-4 pt-3 border-t border-[var(--border)]">
            <p className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider mb-2">{lang === 'pt' ? 'Por Posição' : 'By Position'}</p>
            <div className="h-28">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={faixaBarData} layout="vertical" margin={{ top: 0, right: 8, left: 50, bottom: 0 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="posicao" tick={{ fill: 'var(--muted)', fontSize: 9 }} width={50} tickLine={false} axisLine={false} />
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '10px' }} />
                  <Bar dataKey="≤30" stackId="a" fill="#10b981" />
                  <Bar dataKey="30–50" stackId="a" fill="#3b82f6" />
                  <Bar dataKey=">50" stackId="a" fill="#f59e0b" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
      </div>

      {/* Regional Distribution */}
      <Card title={lang === 'pt' ? 'Distribuição Regional por Posição' : 'Regional Distribution by Position'} refDate={refDate}>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={regionalBarData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="posicao" tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 10 }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '10px' }} />
              <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }} />
              {['Norte','Nordeste','Centro-Oeste','Sudeste','Sul','Exterior'].map((region, i) => (
                <Bar key={region} dataKey={region} stackId="a" fill={REGION_COLORS[i]} radius={i === 5 ? [4,4,0,0] : [0,0,0,0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {locGeneroRows.length > 0 && (
          <div className="mt-6 pt-4 border-t border-[var(--border)]">
            <p className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider mb-3">{lang === 'pt' ? 'Gênero por Região' : 'Gender by Region'}</p>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={locGeneroRows.filter(r => r.Local !== 'Exterior').map(r => ({
                    local: r.Local,
                    [lang === 'pt' ? 'Feminino' : 'Female']: r.Quantidade_Feminino || 0,
                    [lang === 'pt' ? 'Masculino' : 'Male']: r.Quantidade_Masculino || 0,
                    [lang === 'pt' ? 'Não Binário' : 'Non-Binary']: (r.Quantidade_Nao_Binario || 0) + (r.Quantidade_Outros || 0),
                  }))}
                  margin={{ top: 4, right: 8, left: -10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="local" tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '10px' }} />
                  <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }} />
                  <Bar dataKey={lang === 'pt' ? 'Feminino' : 'Female'} stackId="a" fill="#ec4899" />
                  <Bar dataKey={lang === 'pt' ? 'Masculino' : 'Male'} stackId="a" fill="#3b82f6" />
                  <Bar dataKey={lang === 'pt' ? 'Não Binário' : 'Non-Binary'} stackId="a" fill="#8b5cf6" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </Card>

    </div>
  );
}
